import { useEasyMode, EasyModeModalMeta } from "@/contexts/EasyModeContext";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_NAV_CONFIG } from "@/lib/roles";
import { useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import {
  Keyboard,
  X,
  Search,
  IndianRupee,
  CreditCard,
  ArrowRightLeft,
  Receipt,
  Calculator,
  Layers,
  Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { getAccountingPermissions } from "@/lib/accounting/engine";
import { useToast } from "@/hooks/use-toast";
import { ContraVoucherForm } from "@/components/accounting/ContraVoucherForm";
import { PaymentVoucherForm } from "@/components/accounting/PaymentVoucherForm";
import { ReceiptVoucherForm } from "@/components/accounting/ReceiptVoucherForm";
import { GSTInvoiceForm } from "@/components/accounting/GSTInvoiceForm";

interface EasyTask {
  id: string;
  title: string;
  description: string;
  url: string;
  badge: string;
  icon: any;
  iconColor: string;
  keywords: string[];
  modalType?: "contra" | "payment" | "receipt" | "sales" | "purchase";
  transferType?: "deposit" | "withdrawal" | "transfer";
}

const EASY_TASKS: EasyTask[] = [
  {
    id: "receive-money",
    title: "Receive Money",
    description: "Customer collections, sales payments & revenue (F6)",
    url: "/dashboard/journals?type=receipt&action=new",
    badge: "Receipt",
    icon: IndianRupee,
    iconColor: "text-success",
    keywords: ["receive", "receipt", "collect", "customer", "payment", "upi", "cheque", "income"],
    modalType: "receipt",
  },
  {
    id: "pay-money",
    title: "Pay Money",
    description: "Supplier settlements, vendor bills & business expenses (F5)",
    url: "/dashboard/journals?type=payment&action=new",
    badge: "Payment",
    icon: CreditCard,
    iconColor: "text-destructive",
    keywords: ["pay", "payment", "supplier", "vendor", "bill", "expense", "disbursement"],
    modalType: "payment",
  },
  {
    id: "cash-deposit",
    title: "Cash Deposit",
    description: "Deposit physical cash into bank account without income tax (F4)",
    url: "/dashboard/journals?type=contra&action=new",
    badge: "Contra",
    icon: ArrowRightLeft,
    iconColor: "text-accent",
    keywords: ["cash", "deposit", "bank", "contra"],
    modalType: "contra",
    transferType: "deposit",
  },
  {
    id: "cash-withdrawal",
    title: "Cash Withdrawal",
    description: "Withdraw cash from bank account for office / petty cash (F4)",
    url: "/dashboard/journals?type=contra&action=new",
    badge: "Contra",
    icon: ArrowRightLeft,
    iconColor: "text-accent",
    keywords: ["withdraw", "cash", "bank", "contra", "atm"],
    modalType: "contra",
    transferType: "withdrawal",
  },
  {
    id: "bank-transfer",
    title: "Bank Transfer",
    description: "Move funds between company bank accounts (F4)",
    url: "/dashboard/journals?type=contra&action=new",
    badge: "Contra",
    icon: ArrowRightLeft,
    iconColor: "text-accent",
    keywords: ["transfer", "bank", "contra", "neft", "rtgs", "account"],
    modalType: "contra",
    transferType: "transfer",
  },
  {
    id: "new-sale",
    title: "New Sale",
    description: "Create GST sales invoice & customer bill (F8)",
    url: "/dashboard/sales",
    badge: "Sales",
    icon: Receipt,
    iconColor: "text-primary",
    keywords: ["sale", "invoice", "bill", "customer", "gst"],
    modalType: "sales",
  },
  {
    id: "new-purchase",
    title: "New Purchase",
    description: "Record supplier purchase invoice and claim ITC (F9)",
    url: "/dashboard/purchase",
    badge: "Purchase",
    icon: Calculator,
    iconColor: "text-primary",
    keywords: ["purchase", "bill", "vendor", "supplier", "buy"],
    modalType: "purchase",
  },
  {
    id: "journal-entry",
    title: "Journal Entry",
    description: "Manual double-entry adjustment, provision or depreciation (F7)",
    url: "/dashboard/journals?type=journal&action=new",
    badge: "Journal",
    icon: Layers,
    iconColor: "text-primary",
    keywords: ["journal", "adjustment", "double", "entry", "dr", "cr"],
  },
];

export function EasyModeOverlay() {
  const {
    isEasyMode,
    disableEasyMode,
    showCommandPalette,
    setShowCommandPalette,
    activeModal,
    modalMeta,
    openTransactionModal,
    closeTransactionModal,
  } = useEasyMode();
  const { role } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [focusedIdx, setFocusedIdx] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");

  const handleOpenTransaction = useCallback(
    (modalType: "contra" | "payment" | "receipt" | "sales" | "purchase", meta?: EasyModeModalMeta) => {
      // Prevent duplicate modal opening if already open
      if (activeModal === modalType) {
        return;
      }

      // Check permissions based on user role
      const moduleMap: Record<string, string> = {
        contra: "Contra",
        payment: "Payment",
        receipt: "Receipt",
        sales: "Sales",
        purchase: "Purchase",
      };
      const moduleName = moduleMap[modalType];
      const perms = getAccountingPermissions(role, moduleName);

      if (!perms.canCreate) {
        toast({
          title: "Permission Denied",
          description: `Your role (${role || "user"}) does not have permission to create ${moduleName} transactions.`,
          variant: "destructive",
        });
        return;
      }

      openTransactionModal(modalType, meta);
    },
    [role, activeModal, openTransactionModal, toast]
  );

  const allNavItems = useMemo(() => {
    const groups = ROLE_NAV_CONFIG[role] || [];
    return groups.flatMap((g) => g.items);
  }, [role]);

  // Combined tasks and nav items for search
  const filteredTasks = useMemo(() => {
    if (!searchQuery) return EASY_TASKS;
    const q = searchQuery.toLowerCase().trim();
    return EASY_TASKS.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        t.keywords.some((k) => k.includes(q))
    );
  }, [searchQuery]);

  const filteredNav = useMemo(() => {
    if (!searchQuery) return allNavItems;
    const q = searchQuery.toLowerCase().trim();
    return allNavItems.filter((item) => item.title.toLowerCase().includes(q));
  }, [allNavItems, searchQuery]);

  const allSelectable = useMemo(() => {
    return [
      ...filteredTasks.map((t) => ({
        type: "task" as const,
        title: t.title,
        desc: t.description,
        url: t.url,
        badge: t.badge,
        modalType: t.modalType,
        transferType: t.transferType,
      })),
      ...filteredNav.map((n) => ({
        type: "nav" as const,
        title: n.title,
        desc: n.url,
        url: n.url,
        badge: "Page",
        modalType: undefined as "contra" | "payment" | "receipt" | "sales" | "purchase" | undefined,
        transferType: undefined as "deposit" | "withdrawal" | "transfer" | undefined,
      })),
    ];
  }, [filteredTasks, filteredNav]);

  useEffect(() => {
    setFocusedIdx(0);
  }, [allSelectable.length]);

  const navigateToItem = useCallback(
    (url: string) => {
      navigate(url);
      setShowCommandPalette(false);
    },
    [navigate, setShowCommandPalette]
  );

  const handleSelectItem = useCallback(
    (item: (typeof allSelectable)[0]) => {
      setShowCommandPalette(false);
      if (item.modalType) {
        handleOpenTransaction(item.modalType, { defaultTransferType: item.transferType });
      } else {
        navigate(item.url);
      }
    },
    [handleOpenTransaction, navigate, setShowCommandPalette]
  );

  // Global Keyboard Shortcuts in Easy Mode: F4, F5, F6, F8, F9, Ctrl+K
  useEffect(() => {
    if (!isEasyMode) return;

    const isTypingInField = (e: KeyboardEvent): boolean => {
      const target = (e.composedPath ? e.composedPath()[0] : e.target) as HTMLElement | null;
      if (!target) return false;
      const tagName = target.tagName ? target.tagName.toUpperCase() : "";
      if (tagName === "INPUT" || tagName === "TEXTAREA" || tagName === "SELECT") {
        return true;
      }
      if (target.isContentEditable) {
        return true;
      }
      if (target.closest && target.closest('input, textarea, select, [contenteditable="true"]')) {
        return true;
      }
      return false;
    };

    const handler = (e: KeyboardEvent) => {
      // Ctrl+K to open command palette
      if ((e.ctrlKey || e.metaKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setShowCommandPalette(true);
        setSearchQuery("");
        return;
      }

      if (e.key === "Escape" && showCommandPalette) {
        e.preventDefault();
        setShowCommandPalette(false);
        return;
      }

      // Suppress when typing in input, textarea, select, contenteditable
      if (isTypingInField(e)) {
        return;
      }

      // Suppress when modifier keys are pressed to avoid conflicting with browser / OS shortcuts (e.g. Alt+F4)
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) {
        return;
      }

      const key = e.key;
      if (!["F4", "F5", "F6", "F8", "F9"].includes(key)) {
        return;
      }

      // Intercept key and prevent native browser action (e.g. F5 reload, F6 URL bar focus)
      e.preventDefault();
      e.stopPropagation();

      // Prevent duplicate modal opening if shortcut is pressed repeatedly or another modal is open
      if (activeModal !== null) {
        return;
      }

      switch (key) {
        case "F4":
          handleOpenTransaction("contra", { defaultTransferType: "deposit" });
          break;
        case "F5":
          handleOpenTransaction("payment");
          break;
        case "F6":
          handleOpenTransaction("receipt");
          break;
        case "F8":
          handleOpenTransaction("sales");
          break;
        case "F9":
          handleOpenTransaction("purchase");
          break;
      }
    };

    // Use capture phase so we can preventDefault before browser reload or address bar focus
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [isEasyMode, showCommandPalette, activeModal, handleOpenTransaction, setShowCommandPalette]);

  // Keyboard navigation inside command palette
  useEffect(() => {
    if (!showCommandPalette) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIdx((i) => Math.min(i + 1, allSelectable.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIdx((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && allSelectable[focusedIdx]) {
        e.preventDefault();
        handleSelectItem(allSelectable[focusedIdx]);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showCommandPalette, focusedIdx, allSelectable, handleSelectItem]);

  if (!isEasyMode) return null;

  return (
    <>
      {/* Floating Task Bar at Bottom */}
      <div className="fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-slate-200 px-3 py-2 flex items-center justify-between gap-3 text-xs shadow-md">
        {/* Left: Indicator */}
        <div className="flex items-center gap-1.5 shrink-0">
          <Sparkles className="h-4 w-4 text-blue-600 animate-pulse" />
          <span className="font-bold text-slate-800 hidden sm:inline">Easy Mode:</span>
        </div>

        {/* Center: Quick Action Buttons */}
        <div className="flex items-center gap-1.5 overflow-x-auto py-0.5 no-scrollbar">
          <button
            onClick={() => handleOpenTransaction("receipt")}
            className="px-2.5 py-1 rounded-lg bg-emerald-50 hover:bg-emerald-100/80 text-emerald-700 font-semibold text-xs flex items-center gap-1 border border-emerald-200 shrink-0 transition-colors shadow-2xs"
          >
            <IndianRupee className="h-3.5 w-3.5 text-emerald-600" />
            <span>Receive Money</span>
            <kbd className="hidden md:inline font-mono text-[9px] px-1 py-0.2 bg-emerald-100/80 rounded text-emerald-800">F6</kbd>
          </button>

          <button
            onClick={() => handleOpenTransaction("payment")}
            className="px-2.5 py-1 rounded-lg bg-rose-50 hover:bg-rose-100/80 text-rose-700 font-semibold text-xs flex items-center gap-1 border border-rose-200 shrink-0 transition-colors shadow-2xs"
          >
            <CreditCard className="h-3.5 w-3.5 text-rose-600" />
            <span>Pay Money</span>
            <kbd className="hidden md:inline font-mono text-[9px] px-1 py-0.2 bg-rose-100/80 rounded text-rose-800">F5</kbd>
          </button>

          <button
            onClick={() => handleOpenTransaction("contra", { defaultTransferType: "deposit" })}
            className="px-2.5 py-1 rounded-lg bg-teal-50 hover:bg-teal-100/80 text-teal-700 font-semibold text-xs flex items-center gap-1 border border-teal-200 shrink-0 transition-colors shadow-2xs"
          >
            <ArrowRightLeft className="h-3.5 w-3.5 text-teal-600" />
            <span>Cash / Bank Transfer</span>
            <kbd className="hidden md:inline font-mono text-[9px] px-1 py-0.2 bg-teal-100/80 rounded text-teal-800">F4</kbd>
          </button>

          <button
            onClick={() => handleOpenTransaction("sales")}
            className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100/80 text-blue-700 font-semibold text-xs flex items-center gap-1 border border-blue-200 shrink-0 transition-colors shadow-2xs"
          >
            <Receipt className="h-3.5 w-3.5 text-blue-600" />
            <span>New Sale</span>
            <kbd className="hidden md:inline font-mono text-[9px] px-1 py-0.2 bg-blue-100/80 rounded text-blue-800">F8</kbd>
          </button>

          <button
            onClick={() => handleOpenTransaction("purchase")}
            className="px-2.5 py-1 rounded-lg bg-purple-50 hover:bg-purple-100/80 text-purple-700 font-semibold text-xs flex items-center gap-1 border border-purple-200 shrink-0 transition-colors shadow-2xs"
          >
            <Calculator className="h-3.5 w-3.5 text-purple-600" />
            <span>New Purchase</span>
            <kbd className="hidden md:inline font-mono text-[9px] px-1 py-0.2 bg-purple-100/80 rounded text-purple-800">F9</kbd>
          </button>

          <button
            onClick={() => {
              setShowCommandPalette(true);
              setSearchQuery("");
            }}
            className="px-2.5 py-1 rounded-lg bg-slate-100 hover:bg-slate-200/80 text-slate-700 font-semibold text-xs shrink-0 border border-slate-200 transition-colors"
          >
            Ctrl+K All Tasks
          </button>
        </div>

        {/* Right: Exit */}
        <button
          onClick={disableEasyMode}
          className="flex items-center gap-1 text-slate-500 hover:text-slate-900 transition-colors shrink-0 text-xs px-2 py-1 rounded-lg hover:bg-slate-100"
        >
          <X className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Exit</span>
        </button>
      </div>

      {/* Easy Mode Command Palette */}
      {showCommandPalette && (
        <div
          className="fixed inset-0 z-[70] flex items-start justify-center pt-[12vh]"
          onClick={() => setShowCommandPalette(false)}
        >
          <div className="absolute inset-0 bg-slate-900/40" />
          <div
            className="relative w-full max-w-lg bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search */}
            <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/20">
              <Search className="h-4 w-4 text-primary shrink-0" />
              <input
                autoFocus
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="What would you like to do? (e.g. Receive money, Pay bill, Deposit cash)"
                className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
              />
              <kbd className="px-1.5 py-0.5 rounded bg-muted border border-border font-mono text-[10px] text-muted-foreground">
                Esc
              </kbd>
            </div>

            {/* Results */}
            <div className="max-h-96 overflow-y-auto p-2 space-y-1">
              {allSelectable.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">No matching tasks or pages found</p>
              ) : (
                allSelectable.map((item, idx) => {
                  const isFocused = idx === focusedIdx;
                  return (
                    <button
                      key={item.url + idx}
                      onClick={() => handleSelectItem(item)}
                      onMouseEnter={() => setFocusedIdx(idx)}
                      className={cn(
                        "w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-left transition-colors",
                        isFocused ? "bg-primary/10 text-foreground" : "text-muted-foreground hover:bg-muted/50"
                      )}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="flex items-center gap-2">
                          <span className={cn("text-xs font-semibold", isFocused ? "text-primary" : "text-foreground")}>
                            {item.title}
                          </span>
                          <span className="text-[10px] px-1.5 py-0.2 rounded font-mono bg-muted text-muted-foreground border border-border">
                            {item.badge}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground truncate">{item.desc}</p>
                      </div>
                      <kbd className="text-[10px] text-muted-foreground font-mono shrink-0">Enter</kbd>
                    </button>
                  );
                })
              )}
            </div>

            <div className="p-2 border-t border-border bg-muted/10 flex justify-between items-center text-[11px] text-muted-foreground px-4">
              <span>↑↓ to navigate · Enter to select</span>
              <span>AccouPro Easy Accounting</span>
            </div>
          </div>
        </div>
      )}

      {/* 5 Functional Transaction Modals rendered directly on top of the current page */}
      {/* 1. Cash / Bank Transfer (F4) */}
      <ContraVoucherForm
        isOpen={activeModal === "contra"}
        onOpenChange={(op) => {
          if (!op) closeTransactionModal();
        }}
        defaultTransferType={modalMeta.defaultTransferType || "deposit"}
        onSuccess={() => {
          closeTransactionModal();
        }}
      />

      {/* 2. Pay Money (F5) */}
      <PaymentVoucherForm
        isOpen={activeModal === "payment"}
        onOpenChange={(op) => {
          if (!op) closeTransactionModal();
        }}
        onSuccess={() => {
          closeTransactionModal();
        }}
      />

      {/* 3. Receive Money (F6) */}
      <ReceiptVoucherForm
        isOpen={activeModal === "receipt"}
        onOpenChange={(op) => {
          if (!op) closeTransactionModal();
        }}
        onSuccess={() => {
          closeTransactionModal();
        }}
      />

      {/* 4. New Sale / GST Sales Invoice (F8) */}
      <GSTInvoiceForm
        type="Sales"
        isOpen={activeModal === "sales"}
        onOpenChange={(op) => {
          if (!op) closeTransactionModal();
        }}
        onSuccess={() => {
          closeTransactionModal();
        }}
      />

      {/* 5. New Purchase / GST Purchase Bill (F9) */}
      <GSTInvoiceForm
        type="Purchase"
        isOpen={activeModal === "purchase"}
        onOpenChange={(op) => {
          if (!op) closeTransactionModal();
        }}
        onSuccess={() => {
          closeTransactionModal();
        }}
      />
    </>
  );
}